import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Copy, Link2, Loader2, PlugZap, RefreshCw, ShieldAlert, Unplug } from "lucide-react";

const ENTORNOS = [
  { value: "sandbox", label: "Sandbox (pruebas)" },
  { value: "produccion", label: "Producción" },
];
const DIRECCIONES = [
  { value: "cobros_clientes", label: "Cobros de clientes (Payment)" },
  { value: "pagos_carriers", label: "Pagos a carriers (BillPayment / Purchase)" },
];
const ESTADO_STYLES = {
  conectada: "bg-green-100 text-green-800 border-green-300",
  desconectada: "bg-slate-100 text-slate-600 border-slate-300",
  token_vencido: "bg-red-100 text-red-800 border-red-300",
  error: "bg-amber-100 text-amber-800 border-amber-300",
};
const ESTADO_LABELS = {
  conectada: "Conectada", desconectada: "Desconectada",
  token_vencido: "Token vencido — hay que reconectar", error: "Error",
};

export function QuickBooksConnectionCard({ conn, onReload }) {
  const [form, setForm] = useState({
    entorno: conn.entorno, client_id: "", client_secret: "",
    redirect_uri: conn.redirect_uri || "", direcciones_activas: conn.direcciones_activas || [],
  });
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const toggleDireccion = (v) => {
    const cur = form.direcciones_activas;
    setForm({ ...form, direcciones_activas: cur.includes(v) ? cur.filter((d) => d !== v) : [...cur, v] });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { entorno: form.entorno, redirect_uri: form.redirect_uri, direcciones_activas: form.direcciones_activas };
      if (form.client_id) payload.client_id = form.client_id;
      if (form.client_secret) payload.client_secret = form.client_secret;
      await api.put(`/quickbooks/connections/${conn.company_id}`, payload);
      setForm({ ...form, client_id: "", client_secret: "" });
      toast.success("Conexión de QuickBooks guardada");
      onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get(`/quickbooks/connect/${conn.company_id}`);
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setConnecting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm(`¿Desconectar QuickBooks de ${conn.company_nombre}? Se revocan los tokens guardados.`)) return;
    try {
      await api.post(`/quickbooks/connections/${conn.company_id}/disconnect`);
      toast.success("Desconectado");
      onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const testConn = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/quickbooks/connections/${conn.company_id}/test`);
      toast.success(`Conexión OK — empresa: ${data.empresa_nombre || "—"} (zona horaria: ${data.empresa_zona_horaria || "—"})`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/quickbooks/connections/${conn.company_id}/sync`, {});
      toast.success(data.mensaje);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const copyRedirect = async () => {
    try {
      await navigator.clipboard.writeText(form.redirect_uri);
      toast.success("Redirect URI copiado");
    } catch {
      toast.error("No se pudo copiar automáticamente — seleccioná el texto manualmente.");
    }
  };

  return (
    <Card data-testid={`qbo-card-${conn.company_id}`}>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2 font-heading">
          <PlugZap className="h-5 w-5 text-primary" /> {conn.company_nombre}
        </CardTitle>
        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${ESTADO_STYLES[conn.estado] || ""}`}
              data-testid={`qbo-estado-${conn.company_id}`}>
          {ESTADO_LABELS[conn.estado] || conn.estado}
        </span>
      </CardHeader>
      <CardContent className="space-y-5">
        {conn.modo_simulacion && (
          <p className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800">
            Modo simulación activo: sin credenciales cargadas, la pantalla de Coincidencias funciona con datos de
            ejemplo para poder probar el flujo completo.
          </p>
        )}

        {conn.estado === "conectada" && (
          <div className="grid grid-cols-1 gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm sm:grid-cols-2">
            <div><span className="text-muted-foreground">Empresa en QBO:</span> <b data-testid={`qbo-empresa-nombre-${conn.company_id}`}>{conn.empresa_nombre || "—"}</b></div>
            <div><span className="text-muted-foreground">Realm ID:</span> <span className="font-mono">{conn.realm_id || "—"}</span></div>
            <div><span className="text-muted-foreground">Entorno:</span> {ENTORNOS.find((e) => e.value === conn.entorno)?.label}</div>
            <div><span className="text-muted-foreground">Zona horaria empresa:</span> {conn.empresa_zona_horaria || "—"}</div>
            <div><span className="text-muted-foreground">Último refresh exitoso:</span> {conn.ultimo_refresh_exitoso ? new Date(conn.ultimo_refresh_exitoso).toLocaleString("es-MX") : "—"}</div>
            <div><span className="text-muted-foreground">Última sincronización:</span> {conn.ultima_sincronizacion ? new Date(conn.ultima_sincronizacion).toLocaleString("es-MX") : "—"}</div>
          </div>
        )}
        {conn.estado === "token_vencido" && (
          <p className="flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
            <ShieldAlert className="h-4 w-4 shrink-0" /> El refresh token venció o fue revocado por Intuit. Hay que reconectar (botón abajo). Detalle: {conn.ultimo_error || "—"}
          </p>
        )}
        {/* P36 (2026-09-25): si el administrador de QuickBooks abrió el enlace y la autorización falló
            (enlace vencido, Intuit rechazó el código, etc.), el backend lo anota en ultimo_error. Antes
            no quedaba rastro y la conexión decía "Desconectada" sin más. */}
        {conn.estado === "desconectada" && conn.ultimo_error && (
          <p className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800"
             data-testid={`qbo-ultimo-error-${conn.company_id}`}>
            <ShieldAlert className="h-4 w-4 shrink-0" /> {conn.ultimo_error} — generá un enlace nuevo con "Conectar con QuickBooks".
          </p>
        )}

        <div className="grid max-w-xs">
          <Label className="text-xs text-muted-foreground">Entorno</Label>
          <select className="mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.entorno} onChange={(e) => setForm({ ...form, entorno: e.target.value })}
                  data-testid={`qbo-entorno-${conn.company_id}`}>
            {ENTORNOS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
          <p className="mt-1 text-xs text-muted-foreground font-mono">{conn.base_url_api}</p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs text-muted-foreground">
              Client ID {conn.client_id_configurado && <span className="text-green-700">(configurado, •{conn.client_id_last4})</span>}
            </Label>
            <Input className="mt-1 font-mono" type="password" placeholder="ABxxxxxxxxxxxxxxx"
                  value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  data-testid={`qbo-client-id-${conn.company_id}`} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">
              Client Secret {conn.client_secret_configurado && <span className="text-green-700">(configurado, •{conn.client_secret_last4})</span>}
            </Label>
            <Input className="mt-1 font-mono" type="password" value={form.client_secret}
                  onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                  data-testid={`qbo-client-secret-${conn.company_id}`} />
          </div>
        </div>

        <div>
          <Label className="text-xs text-muted-foreground flex items-center gap-1"><Link2 className="h-3 w-3" /> Redirect URI (registrá este valor tal cual en tu app de Intuit)</Label>
          <div className="mt-1 flex gap-2">
            <Input className="font-mono text-xs" value={form.redirect_uri}
                  onChange={(e) => setForm({ ...form, redirect_uri: e.target.value })}
                  data-testid={`qbo-redirect-uri-${conn.company_id}`} />
            <Button type="button" variant="outline" size="icon" onClick={copyRedirect} data-testid={`qbo-copy-redirect-${conn.company_id}`}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          {conn.redirect_uri_advertencia && (
            <p className="mt-1 flex items-center gap-1 text-xs text-amber-700" data-testid={`qbo-redirect-warning-${conn.company_id}`}>
              <ShieldAlert className="h-3.5 w-3.5 shrink-0" /> {conn.redirect_uri_advertencia}
            </p>
          )}
        </div>

        <div>
          <Label className="text-xs text-muted-foreground">Direcciones a sincronizar</Label>
          <div className="mt-1 flex flex-col gap-2">
            {DIRECCIONES.map((d) => (
              <label key={d.value} className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.direcciones_activas.includes(d.value)} onCheckedChange={() => toggleDireccion(d.value)}
                          data-testid={`qbo-direccion-${d.value}-${conn.company_id}`} />
                {d.label}
              </label>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={saving} data-testid={`qbo-save-${conn.company_id}`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
          </Button>
          <Button variant="outline" onClick={connect} disabled={connecting} className="gap-2" data-testid={`qbo-connect-${conn.company_id}`}>
            <PlugZap className="h-4 w-4" /> {conn.estado === "conectada" ? "Reconectar" : "Conectar con QuickBooks"}
          </Button>
          {conn.estado === "conectada" && (
            <>
              <Button variant="outline" onClick={testConn} disabled={testing} className="gap-2" data-testid={`qbo-test-${conn.company_id}`}>
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Probar conexión
              </Button>
              <Button variant="outline" onClick={syncNow} disabled={syncing} className="gap-2" data-testid={`qbo-sync-${conn.company_id}`}>
                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sincronizar ahora
              </Button>
              <Button variant="outline" onClick={disconnect} className="gap-2 text-red-600" data-testid={`qbo-disconnect-${conn.company_id}`}>
                <Unplug className="h-4 w-4" /> Desconectar
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
